<template>
  <div
    class="user-account"
    :class="[`type-${type}`, platform]"
    data-t="user-account"
  >
    <StScrolledShadowContainer v-if="isHeader" :is-scrolled="isScrolled">
      <p class="header-account">
        {{ t('header.activeBalance') }}
      </p>
    </StScrolledShadowContainer>
    <div class="header-panel" @click.stop>
      <span class="header-title">
        {{ t('payments.wallet.myAssets') }}
      </span>
      <div class="header-actions">
        <StButton
          icon="gear-solid"
          is-left-icon
          :type="typeManageAssets"
          :label="t('payments.wallet.manageAssets')"
          @click="isShownManageAssets = true"
        />
        <div class="header-toggle-container">
          <span class="header-subtitle">
            {{ t('payments.wallet.hideZero') }}
          </span>
          <StToggle v-model="hideNulls" size="s" data-t="header-panel-toggle" />
        </div>
      </div>
    </div>
    <div v-if="!accountsLength" class="empty-accounts">
      <img :src="emptyAccounts" class="empty-image" alt="empty" />
      <span class="empty-accounts-text">
        {{ t('payments.wallet.hiddenAccounts') }}
      </span>
    </div>
    <div v-else ref="currencies" class="currencies-lists">
      <AccountList
        v-if="realAccounts.fiat.length"
        :accounts="realAccounts.fiat"
        :type
        :title="t('payments.wallet.cash')"
        @account-select="emit('accountSelect')"
      />
      <AccountList
        v-if="realAccounts.crypto.length"
        :accounts="realAccounts.crypto"
        :type
        :title="t('payments.wallet.crypto')"
        @account-select="emit('accountSelect')"
      />
    </div>
    <StDivider v-if="isHeader" :spacing="50" />
    <SiteAvailableCrypto
      v-if="!hideSiteCrypto"
      class="site-currencies"
      background="primary"
    />
    <ManageAssetsWrapper v-model="isShownManageAssets" />
  </div>
</template>

<script setup lang="ts">
import { useAccountsStore } from '../../stores/useAccountsStore'
import emptyAccounts from './assets/empty.svg'
import { useAllowedAssets } from '../../composables/useAllowedAssets'
import AccountList from './parts/AccountList/AccountList.vue'
import ManageAssetsWrapper from '../ManageAssets/ManageAssetsWrapper.vue'

interface Props {
  type: 'header' | 'wallet' | 'widget'
  hideSiteCrypto?: boolean
}

const emit = defineEmits<{
  accountSelect: []
}>()

const currencies = useTemplateRef('currencies')
const { isScrolled } = useScrolled({ element: currencies })
const isShownManageAssets = ref(false)

const { type, hideSiteCrypto } = defineProps<Props>()

const accountStore = useAccountsStore()

const { hideNulls } = storeToRefs(accountStore)
const { realAccountsVisibleForUser: realAccounts } = useAllowedAssets({
  omitZeroBalanceFiltration: false,
})

const { t } = useI18n()
const { platform, isMobile } = usePlatform()

const accountsLength = computed(
  () => realAccounts.value.crypto.length || realAccounts.value.fiat.length,
)

const typeManageAssets = computed(() =>
  type === 'wallet' && !isMobile.value ? 'text-primary' : 'text-tertiary',
)

const isHeader = computed(() => type === 'header')

// Данный костыль сделан из-за модалки ManageAssets, открывающейся в тултипе.
// При открытой модалке необходимо отключать clickOutside на тултипе
defineExpose({
  isShownManageAssets,
})
</script>

<style scoped>
.user-account {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--spacing-100);
}

.header-panel {
  order: 1;
  margin-top: auto;
}

.header-account {
  margin: 0;
  padding: var(--spacing-125) var(--spacing-150);
  font: var(--desktop-text-md-medium);
  border-bottom: var(--border-width-light) solid var(--border-secondary);
}

.header-title {
  font: var(--desktop-text-lg-semibold);
}

.header-actions {
  display: flex;
  gap: var(--spacing-200);
  align-items: center;
  justify-content: space-between;
}

.header-toggle-container {
  display: flex;
  gap: var(--spacing-075);
  align-items: center;
}

.header-subtitle {
  font: var(--desktop-text-sm-medium);
  color: var(--text-tertiary);
}

.currency-header-left {
  padding-left: var(--spacing-400);
}

.currency-header-right {
  gap: 0;
  justify-content: flex-end;
  /* stylelint-disable */
  padding-right: 103px;
  /* stylelint-enable */
}

.empty-accounts {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-300);
  align-items: center;
  justify-content: center;

  /* stylelint-disable */
  margin: 20% 0;
  /* stylelint-enable */
}

.empty-accounts-text {
  font: var(--desktop-text-md-semibold);
  color: var(--text-tertiary);
  text-align: center;
}

.currencies-lists {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);
}

.site-currencies {
  margin: var(--spacing-200) 0 calc(-1 * var(--spacing-200));
}

.type-header {
  &.user-account {
    gap: 0;
    max-width: 360px;
    max-height: 560px;
  }

  .currencies-lists {
    overflow: hidden auto;
    gap: 0;
  }

  .header-panel {
    padding: var(--spacing-150);
  }

  .header-title {
    display: none;
  }

  .empty-image {
    width: 200px;
  }

  .empty-accounts-text {
    width: 300px;
    font: var(--mobile-text-content-regular);
  }
}

.mobile {
  &.user-account {
    gap: 0;
  }

  .currencies-lists {
    padding: var(--spacing-200) var(--spacing-200) var(--spacing-150);
  }

  .header-panel {
    padding: var(--spacing-200);
    background: var(--background-base);
    border-top: var(--border-width-light) solid var(--border-primary);
  }

  .header-title {
    display: none;
  }

  .header-subtitle {
    font: var(--mobile-text-content-regular);
    color: var(--text-primary);
  }

  .empty-image {
    width: 200px;
  }

  .empty-accounts-text {
    width: 300px;
    font: var(--mobile-text-content-regular);
  }
}

.type-widget {
  &.user-account {
    height: calc(100% - 48.5px);
    background-color: var(--background-base);
  }

  .currencies-lists {
    overflow: hidden auto;
  }
}

.type-wallet.mobile {
  .header-panel {
    position: sticky;
    z-index: 100;
    right: 0;
    bottom: 64.5px;
    left: 0;
  }
}

.type-wallet.desktop {
  .header-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    order: 0;
  }
}
</style>
